import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const persistUserSession = (user) => {
    setCurrentUser(user);
    setIsAuthenticated(true);
    localStorage.setItem('nexohub_user', JSON.stringify(user));
  };

  const syncCurrentUser = (latestUsers) => {
    if (!currentUser?.id || !Array.isArray(latestUsers)) return null;

    const refreshedUser = latestUsers.find((user) => user.id === currentUser.id) || null;
    if (!refreshedUser) return null;

    const currentSnapshot = JSON.stringify({
      ...currentUser,
      permissions: currentUser.permissions || [],
      insurerPercentages: currentUser.insurerPercentages || {},
      insurerKmPrices: currentUser.insurerKmPrices || {},
      sourceMappings: currentUser.sourceMappings || {},
      scheduleSettings: currentUser.scheduleSettings || {},
    });

    const refreshedSnapshot = JSON.stringify({
      ...refreshedUser,
      permissions: refreshedUser.permissions || [],
      insurerPercentages: refreshedUser.insurerPercentages || {},
      insurerKmPrices: refreshedUser.insurerKmPrices || {},
      sourceMappings: refreshedUser.sourceMappings || {},
      scheduleSettings: refreshedUser.scheduleSettings || {},
    });

    if (currentSnapshot !== refreshedSnapshot) {
      persistUserSession(refreshedUser);
    }

    return refreshedUser;
  };

  const normalizeUser = (user) => ({
    ...user,
    active: user?.active !== false,
  });

  useEffect(() => {
    const storedUser = localStorage.getItem('nexohub_user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setCurrentUser(user);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('nexohub_user');
      }
    }

    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users`);
      const latestUsers = (res.data || []).map(normalizeUser);
      setUsers(latestUsers);
      syncCurrentUser(latestUsers);
      return latestUsers;
    } catch (error) {
      console.error('Error fetching users:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const login = (user) => {
    persistUserSession(user);
  };

  const logout = () => {
    setCurrentUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('nexohub_user');
  };

  const refreshCurrentUser = async () => {
    if (!currentUser?.id) return null;

    const latestUsers = await fetchUsers();
    const refreshedUser = latestUsers.find((user) => user.id === currentUser.id) || null;

    if (refreshedUser) {
      persistUserSession(refreshedUser);
    }

    return refreshedUser;
  };

  const changePassword = async ({ email, currentPassword, newPassword }) => {
    const res = await axios.post(`${API_URL}/api/change-password`, {
      email,
      currentPassword,
      newPassword,
    });

    if (res.data?.user) {
      persistUserSession(res.data.user);
      await fetchUsers();
    }

    return res.data;
  };

  useEffect(() => {
    if (!currentUser?.id) return undefined;

    let isMounted = true;

    const refreshCurrentSession = async () => {
      if (!isMounted) return;
      const latestUsers = await fetchUsers();
      if (!isMounted || !latestUsers.length) return;
      syncCurrentUser(latestUsers);
    };

    refreshCurrentSession();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshCurrentSession();
      }
    };

    window.addEventListener('focus', refreshCurrentSession);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const intervalId = window.setInterval(refreshCurrentSession, 30000);

    return () => {
      isMounted = false;
      window.removeEventListener('focus', refreshCurrentSession);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [currentUser?.id]);

  const addUser = async (user) => {
    const payload = { ...user, active: user.active !== false };
    const res = await axios.post(`${API_URL}/api/users`, payload);
    const createdUser = normalizeUser(res.data);
    setUsers((prev) => [...prev, createdUser]);
    return createdUser;
  };

  const deleteUser = async (userId) => {
    await axios.delete(`${API_URL}/api/users/${userId}`);
    setUsers((prev) => prev.filter((user) => user.id !== userId));
  };

  const updateUser = async (user) => {
    const res = await axios.put(`${API_URL}/api/users/${user.id}`, {
      ...user,
      active: user.active !== false,
    });

    const savedUser = normalizeUser(res.data);
    setUsers((prev) => prev.map((item) => (item.id === savedUser.id ? savedUser : item)));

    if (currentUser?.id === savedUser.id) {
      persistUserSession(savedUser);
    }

    return savedUser;
  };

  const normalizedRole = (currentUser?.role || '').toLowerCase();
  const isAdmin = normalizedRole === 'admin';
  const isManager = isAdmin || normalizedRole === 'office' || normalizedRole === 'oficina';
  const isTechnician = normalizedRole === 'collaborator' || normalizedRole === 'colaborador';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        users,
        login,
        logout,
        addUser,
        deleteUser,
        updateUser,
        changePassword,
        refreshCurrentUser,
        isAdmin,
        isManager,
        isTechnician,
        isAuthenticated,
        fetchUsers,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
